import { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

interface ImageGalleryProps {
  images: Array<{
    url: string; // Now expects signed URLs directly
    label?: string;
    alt?: string;
  }>;
}

export function ImageGallery({ images }: ImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (images.length === 0) {
    return null;
  }

  const handlePrevious = () => {
    if (selectedIndex === null) return;
    setSelectedIndex((selectedIndex - 1 + images.length) % images.length);
  };

  const handleNext = () => {
    if (selectedIndex === null) return;
    setSelectedIndex((selectedIndex + 1) % images.length);
  };

  const handleClose = () => {
    setSelectedIndex(null);
  };

  return (
    <>
      {/* Film-reel style thumbnail strip */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {images.map((image, index) => (
          <button
            key={index}
            onClick={() => setSelectedIndex(index)}
            className="relative flex-shrink-0 group"
          >
            <div className="w-24 h-24 rounded border-2 border-border hover:border-primary transition-colors overflow-hidden bg-background-subtle">
              <img
                src={image.url}
                alt={image.alt || image.label || `Image ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  console.error('Failed to load image:', image.url);
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
                onLoad={() => console.log('Successfully loaded:', image.url)}
              />
            </div>
            {image.label && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs px-1 py-0.5 truncate">
                {image.label}
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Lightbox modal */}
      {selectedIndex !== null && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded transition-colors"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Previous button */}
          {images.length > 1 && (
            <button
              onClick={handlePrevious}
              className="absolute left-4 p-2 text-white hover:bg-white/10 rounded transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
          )}

          {/* Main image */}
          <div className="max-w-7xl max-h-[90vh] p-4">
            <img
              src={images[selectedIndex].url}
              alt={images[selectedIndex].alt || images[selectedIndex].label || `Image ${selectedIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onError={() => {
                console.error('Failed to load lightbox image:', images[selectedIndex].url);
              }}
            />
            {images[selectedIndex].label && (
              <div className="text-center text-white mt-4 text-lg">
                {images[selectedIndex].label}
              </div>
            )}
          </div>

          {/* Next button */}
          {images.length > 1 && (
            <button
              onClick={handleNext}
              className="absolute right-4 p-2 text-white hover:bg-white/10 rounded transition-colors"
              aria-label="Next image"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          )}

          {/* Image counter */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded">
              {selectedIndex + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </>
  );
}
